import * as React from 'react';
import DatePicker from 'react-datepicker';
import {isAfter} from 'date-fns';
import Expando from '../../shared/Expando';
import {Inbound} from '../shared/InboundInterfaces';
import InboundService from '../../shared/services/InboundService';
import ConfirmInboundArrivalModal from './ConfirmInboundArrivalModal';
import {InboundApiResponse} from './InboundDeliveryDetails';

interface Props {
  inboundService: InboundService;
  inbound: Inbound;
  /** Callback function to update the errors in the parent component. */
  handleApiError: (errors: string[], response: InboundApiResponse) => void;
  /** Callback function to reload inbound details */
  reloadInbound: () => void;
}

const InboundArrivalStep: React.FC<Props> = (props) => {
  const {inbound, inboundService} = props;
  const [showExpando, setShowExpando] = React.useState(true);
  const [showModal, setShowModal] = React.useState(false);
  const [selectedArrivalTime, setSelectedArrivalTime] = React.useState<Date | null>(null);

  const toggleExpando = () => {
    setShowExpando((prev) => !prev);
  };

  const updateSelectedArrivalTime = (date: Date) => {
    setSelectedArrivalTime(date);
  };

  const toLocalISODateTimeString = (date: Date): string => {
    const offset = date.getTimezoneOffset() * 60000; // Convert minutes to milliseconds
    return new Date(date.getTime() - offset).toISOString().slice(0, -1);
  };

  const handleConfirmClick = () => {
    const moveDate = inbound.expectedArrivalDate;

    // if the supplied expected inbound date is in the future,
    // show the confirmation dialog instead of immediately confirming
    const today = new Date();
    if (isAfter(moveDate, today)) {
      openModal();
    } else if (!isInboundArrived()) {
      updateInboundArrivalTime();
    }
  };

  const addActualArrivalTimeStep = {
    title: 'Enter Inbound Container Arrival Time',
    content: (
      <div style={{display: 'flex', alignItems: 'center', gap: '20px'}}>
        <span>
          <DatePicker
            selected={selectedArrivalTime}
            onChange={updateSelectedArrivalTime}
            minDate={new Date(new Date().getFullYear(), new Date().getMonth(), 1)}
            timeIntervals={15}
            showTimeSelect
            dateFormat="MMM dd - h:mm aa"
            placeholderText="Date/Time"
          />
        </span>
        <button className="btn cta" onClick={handleConfirmClick} disabled={!selectedArrivalTime}>
          <i className="fa fa-check" aria-hidden="true"></i>
          &nbsp;Confirm Arrival
        </button>
      </div>
    )
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const openModal = () => {
    setShowModal(true);
  };

  const updateInboundArrivalTime = async () => {
    const response = await inboundService.patchInboundById(inbound.id, {
      arrivalDatetime: toLocalISODateTimeString(selectedArrivalTime)
    });

    if (response?.errors?.length > 0) {
      const errors = [`There was an error confirming inbound #${inbound.id} for receipt`];
      props.handleApiError(errors, response);
    } else {
      props.handleApiError([], undefined);
      props.reloadInbound();
    }
    closeModal();
  };

  const isInboundArrived = () => {
    return !!inbound.actualArrivalTime;
  };

  return (
    <React.Fragment>
      <Expando
        index={1}
        headerIcon={<span className="workflow-step-index">{1}</span>}
        headerText={addActualArrivalTimeStep.title}
        open={showExpando}
        onClick={toggleExpando}
      >
        {addActualArrivalTimeStep.content}
      </Expando>

      <ConfirmInboundArrivalModal
        showModal={showModal}
        toggleModal={closeModal}
        onConfirmLpnDropoff={() => updateInboundArrivalTime()}
      />
    </React.Fragment>
  );
};

export default InboundArrivalStep;
